import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { Todo, TodoDetails, Response } from '../../types';
import { getNotificationInstance, notifySuccess, closeNotification, NotifyActionLink } from '../utils/notification';
import { appNavigate, setArchiveView } from '../utils/navigation';
import React from 'react';

const baseQuery = fetchBaseQuery({ baseUrl: '/api/todos' });

function getErrorDescription(error: unknown): string {
    if (error && typeof error === 'object' && 'data' in error) {
        const data = (error as { data?: { message?: unknown } }).data;
        if (data && typeof data.message === 'string' && data.message && data.message !== 'error') {
            return data.message;
        }
    }
    return 'Произошла ошибка при выполнении запроса';
}

// @ts-ignore
const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
    const notification = getNotificationInstance();
    const requestUrl = typeof args === 'string' ? args : args?.url;
    const silent = Boolean(extraOptions?.silent)
        || (typeof requestUrl === 'string' && requestUrl.includes('resolve-tracker-issue'));

    const result = await baseQuery(args, api, extraOptions);
    if (result.error && !silent) {
        const description = getErrorDescription(result.error);
        if (notification) {
            notification.error({
                message: 'Ошибка',
                description,
                placement: 'topRight',
            });
        }
        else {
            alert(description);
        }
    }
    return result;
};

export const todosSlice = createApi({
    reducerPath: 'todosApi',

    baseQuery: baseQueryWithErrorHandling,

    tagTypes: ['Todos', 'ArchivedTodos'],

    endpoints: (builder) => ({
        getTodos: builder.query<Response<{ todos: Todo[] }> , { projectUrl: string }>({
            query: (request) => ({
                url: '/get-todos',
                method: 'POST',
                body: request,
            }),
            providesTags: ['Todos'],
        }),

        getArchivedTodos: builder.query<Response<{ todos: Todo[] }>, { projectUrl: string }>({
            query: (request) => ({
                url: '/get-archived-todos',
                method: 'POST',
                body: request,
            }),
            providesTags: ['ArchivedTodos'],
        }),

        getTodoById: builder.query<TodoDetails, { todoId: number }>({
            query: ({ todoId }) => `/todo/description/${ todoId }`,
        }),

        resolveTrackerIssue: builder.mutation<
            Response<{ trackerKey: string, summary: string }>,
            { trackerLink: string }
        >({
            query: (request) => ({
                url: '/resolve-tracker-issue',
                method: 'POST',
                body: request,
            }),
            extraOptions: { silent: true },
        }),

        createTodo: builder.mutation<
            Response<{ todoId: number, todoName: string, trackerKey: string | null }>,
            { todoName: string, projectUrl: string, trackerLink?: string }
        >({
            query: (request) => ({
                url: '/create-todo',
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ projectUrl }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const created = data?.message;
                    if (!created?.todoId) {
                        return;
                    }
                    dispatch(
                        todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                            if (!draft.message.todos.some((t) => Number(t.todoId) === Number(created.todoId))) {
                                draft.message.todos.unshift({
                                    todoId: Number(created.todoId),
                                    todoName: created.todoName,
                                    trackerKey: created.trackerKey ?? null,
                                    reportPath: null,
                                });
                            }
                        }),
                    );
                } catch {
                    // notification already shown
                }
            },
        }),

        updateTodo: builder.mutation<
            Response<{ todoId: number, todoName: string, trackerKey: string | null }>,
            { todoId: number, projectUrl: string, todoName?: string, trackerLink?: string | null }
        >({
            query: (request) => ({
                url: '/update-todo',
                method: 'POST',
                body: request,
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                const { todoId, projectUrl, trackerLink } = arg;
                const trackerChanged = Object.prototype.hasOwnProperty.call(arg, 'trackerLink');
                try {
                    const { data } = await queryFulfilled;
                    const updated = data?.message;
                    if (!updated) {
                        return;
                    }
                    dispatch(
                        todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                            const todo = draft.message.todos.find((t) => Number(t.todoId) === Number(todoId));
                            if (todo) {
                                if (updated.todoName != null) {
                                    todo.todoName = updated.todoName;
                                }
                                todo.trackerKey = updated.trackerKey ?? null;
                            }
                        }),
                    );
                    dispatch(
                        todosSlice.util.updateQueryData('getTodoById', { todoId: Number(todoId) }, (draft) => {
                            if (updated.todoName != null) {
                                draft.todoName = updated.todoName;
                            }
                            draft.trackerKey = updated.trackerKey ?? null;
                        }),
                    );
                    if (trackerChanged) {
                        notifySuccess(
                            trackerLink == null || String(trackerLink).trim() === ''
                                ? 'Эпик отвязан от Яндекс Трекера'
                                : 'Эпик привязан к Яндекс Трекеру',
                        );
                    }
                } catch {
                    // notification already shown
                }
            },
        }),

        archiveTodo: builder.mutation<
            Response,
            { todoId: number; projectUrl: string; todoName?: string }
        >({
            query: ({ todoId, projectUrl }) => ({
                url: '/archive-todo',
                method: 'POST',
                body: { todoId, projectUrl },
            }),
            async onQueryStarted({ todoId, projectUrl, todoName }, { dispatch, queryFulfilled }) {
                const activePatch = dispatch(
                    todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                        draft.message.todos = draft.message.todos.filter(
                            (t) => Number(t.todoId) !== Number(todoId),
                        );
                    }),
                );

                try {
                    await queryFulfilled;
                    dispatch(todosSlice.util.invalidateTags(['ArchivedTodos']));

                    const label = todoName?.trim() || `Эпик ${todoId}`;
                    const key = `archive-${todoId}-${Date.now()}`;
                    notifySuccess(
                        React.createElement(
                            React.Fragment,
                            null,
                            `Эпик «${label}» отправлен в архив.`,
                            React.createElement(NotifyActionLink, {
                                onClick: () => {
                                    closeNotification(key);
                                    dispatch(
                                        todosSlice.endpoints.restoreTodo.initiate({
                                            todoId,
                                            projectUrl,
                                            todoName: label,
                                            silentSuccess: true,
                                        }),
                                    );
                                },
                                children: 'вернуть из архива',
                            }),
                        ),
                        { key, duration: 8 },
                    );
                } catch {
                    activePatch.undo();
                }
            },
        }),

        restoreTodo: builder.mutation<
            Response<{ todoId: number; todoName: string; trackerKey: string | null }>,
            {
                todoId: number;
                projectUrl: string;
                todoName?: string;
                trackerKey?: string | null;
                silentSuccess?: boolean;
            }
        >({
            query: ({ todoId, projectUrl }) => ({
                url: '/restore-todo',
                method: 'POST',
                body: { todoId, projectUrl },
            }),
            async onQueryStarted(
                { todoId, projectUrl, todoName, trackerKey, silentSuccess },
                { dispatch, queryFulfilled },
            ) {
                const archivedPatch = dispatch(
                    todosSlice.util.updateQueryData('getArchivedTodos', { projectUrl }, (draft) => {
                        draft.message.todos = draft.message.todos.filter(
                            (t) => Number(t.todoId) !== Number(todoId),
                        );
                    }),
                );

                const activePatch = dispatch(
                    todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                        const existing = draft.message.todos.find(
                            (t) => Number(t.todoId) === Number(todoId),
                        );
                        if (existing) {
                            if (todoName) {
                                existing.todoName = todoName;
                            }
                            if (trackerKey !== undefined) {
                                existing.trackerKey = trackerKey ?? null;
                            }
                        } else {
                            draft.message.todos.unshift({
                                todoId: Number(todoId),
                                todoName: todoName || `Эпик ${todoId}`,
                                trackerKey: trackerKey ?? null,
                                reportPath: null,
                            });
                        }
                    }),
                );

                try {
                    const { data } = await queryFulfilled;
                    const restored = data?.message;
                    const resolvedTrackerKey = (typeof restored === 'object' && restored)
                        ? (restored.trackerKey ?? trackerKey ?? null)
                        : (trackerKey ?? null);
                    const resolvedName = (typeof restored === 'object' && restored?.todoName)
                        ? restored.todoName
                        : todoName;

                    dispatch(
                        todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                            const existing = draft.message.todos.find(
                                (t) => Number(t.todoId) === Number(todoId),
                            );
                            if (!existing) {
                                return;
                            }
                            if (resolvedName) {
                                existing.todoName = resolvedName;
                            }
                            existing.trackerKey = resolvedTrackerKey;
                        }),
                    );
                    dispatch(
                        todosSlice.util.updateQueryData('getTodoById', { todoId: Number(todoId) }, (draft) => {
                            draft.archivedAt = null;
                            if (typeof restored === 'object' && restored) {
                                if (restored.todoName) {
                                    draft.todoName = restored.todoName;
                                }
                                draft.trackerKey = restored.trackerKey ?? null;
                            } else if (trackerKey !== undefined) {
                                draft.trackerKey = trackerKey ?? null;
                            }
                        }),
                    );

                    if (!silentSuccess) {
                        const label = resolvedName?.trim() || `Эпик ${todoId}`;
                        const key = `restore-${todoId}-${Date.now()}`;
                        notifySuccess(
                            React.createElement(
                                React.Fragment,
                                null,
                                `Эпик «${label}» восстановлен из архива.`,
                                React.createElement(NotifyActionLink, {
                                    onClick: () => {
                                        closeNotification(key);
                                        setArchiveView(false);
                                        appNavigate(`/projects/${projectUrl}/${todoId}`);
                                    },
                                    children: 'перейти к эпику',
                                }),
                            ),
                            { key, duration: 8 },
                        );
                    }
                } catch {
                    archivedPatch.undo();
                    activePatch.undo();
                }
            },
        }),

        uploadTodoReport: builder.mutation<
            Response<{ todoId: number; reportPath: string }>,
            { todoId: number; projectUrl: string; file: File }
        >({
            query: (request) => {
                const formData = new FormData();
                formData.append('file', request.file);
                formData.append('todoId', request.todoId.toString());
                formData.append('projectUrl', request.projectUrl);

                return {
                    url: '/upload-todo-report',
                    method: 'POST',
                    body: formData,
                };
            },
            async onQueryStarted({ todoId, projectUrl }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const reportPath = data?.message?.reportPath ?? null;
                    if (!reportPath) {
                        return;
                    }

                    const patch = (todo: { reportPath?: string | null }) => {
                        todo.reportPath = reportPath;
                    };

                    dispatch(
                        todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                            const todo = draft.message.todos.find((t) => Number(t.todoId) === Number(todoId));
                            if (todo) {
                                patch(todo);
                            }
                        }),
                    );
                    dispatch(
                        todosSlice.util.updateQueryData('getArchivedTodos', { projectUrl }, (draft) => {
                            const todo = draft.message?.todos?.find((t) => Number(t.todoId) === Number(todoId));
                            if (todo) {
                                patch(todo);
                            }
                        }),
                    );
                    dispatch(
                        todosSlice.util.updateQueryData('getTodoById', { todoId: Number(todoId) }, (draft) => {
                            draft.reportPath = reportPath;
                        }),
                    );
                    notifySuccess('HTML-отчёт загружен');
                } catch {
                    // notification already shown
                }
            },
        }),

        deleteTodoReport: builder.mutation<
            Response<{ todoId: number; reportPath: null }>,
            { todoId: number; projectUrl: string }
        >({
            query: (request) => ({
                url: '/delete-todo-report',
                method: 'POST',
                body: request,
            }),
            async onQueryStarted({ todoId, projectUrl }, { dispatch, queryFulfilled }) {
                const patchReport = (todo: { reportPath?: string | null }) => {
                    todo.reportPath = null;
                };

                const activePatch = dispatch(
                    todosSlice.util.updateQueryData('getTodos', { projectUrl }, (draft) => {
                        const todo = draft.message.todos.find((t) => Number(t.todoId) === Number(todoId));
                        if (todo) {
                            patchReport(todo);
                        }
                    }),
                );
                const archivedPatch = dispatch(
                    todosSlice.util.updateQueryData('getArchivedTodos', { projectUrl }, (draft) => {
                        const todo = draft.message?.todos?.find((t) => Number(t.todoId) === Number(todoId));
                        if (todo) {
                            patchReport(todo);
                        }
                    }),
                );
                const detailPatch = dispatch(
                    todosSlice.util.updateQueryData('getTodoById', { todoId: Number(todoId) }, (draft) => {
                        draft.reportPath = null;
                    }),
                );

                try {
                    await queryFulfilled;
                    notifySuccess('HTML-отчёт удалён');
                } catch {
                    activePatch.undo();
                    archivedPatch.undo();
                    detailPatch.undo();
                }
            },
        }),
    }),
});

export const {
    useGetTodosQuery,
    useGetArchivedTodosQuery,
    useGetTodoByIdQuery,

    useResolveTrackerIssueMutation,
    useCreateTodoMutation,
    useUpdateTodoMutation,
    useArchiveTodoMutation,
    useRestoreTodoMutation,
    useUploadTodoReportMutation,
    useDeleteTodoReportMutation,
} = todosSlice;
